import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getSurfaceBorder, getSurfaceColor, radii } from '../../theme/tokens';
import {
  HANDLE_SIMILARITY_BASE,
  IMAGE_ADJUSTMENT_RULES,
  METADATA_SIGNAL_RULES,
  type ScoringRuleRow,
} from './scoringCriteria';
import type { UiEvidence } from './types';

const RuleTable = ({ rows }: { rows: ScoringRuleRow[] }) => {
  const theme = useTheme();
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Signal</TableCell>
          <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Condition</TableCell>
          <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700 }}>
            Score impact
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            <TableCell sx={{ fontSize: 12, fontWeight: row.signal ? 600 : 400 }}>{row.signal}</TableCell>
            <TableCell sx={{ fontSize: 12, color: theme.palette.text.secondary }}>{row.condition}</TableCell>
            <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>
              {row.impact}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const ScoringGuide = ({ evidence }: { evidence: UiEvidence | null }) => {
  const theme = useTheme();

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        backgroundColor: getSurfaceColor(theme, 'subtle'),
        border: `1px solid ${getSurfaceBorder(theme)}`,
        borderRadius: `${radii.md}px !important`,
        '&::before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>How scoring works</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: theme.palette.text.secondary, mb: 0.5 }}>
          1. HANDLE SIMILARITY (BASE SCORE)
        </Typography>
        <Box sx={{ mb: 2, overflowX: 'auto' }}>
          <RuleTable rows={HANDLE_SIMILARITY_BASE} />
        </Box>

        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: theme.palette.text.secondary, mb: 0.5 }}>
          2. METADATA SIGNALS
        </Typography>
        <Box sx={{ mb: 2, overflowX: 'auto' }}>
          <RuleTable rows={METADATA_SIGNAL_RULES} />
        </Box>

        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: theme.palette.text.secondary, mb: 0.5 }}>
          3. IMAGE EVIDENCE ADJUSTMENT
        </Typography>
        <Box sx={{ mb: 2, overflowX: 'auto' }}>
          <RuleTable rows={IMAGE_ADJUSTMENT_RULES} />
        </Box>

        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: theme.palette.text.secondary, mb: 0.5 }}>
          4. FINAL MATCH SCORE CALCULATION
        </Typography>
        <Typography sx={{ fontSize: 12.5, mb: 0.5 }}>
          Metadata score = handle similarity base + the sum of every applicable metadata signal above.
        </Typography>
        <Typography sx={{ fontSize: 12.5, mb: 1 }}>
          Final match score = metadata score + image evidence adjustment.
        </Typography>
        {evidence && (
          <Typography sx={{ fontSize: 12.5, fontFamily: 'monospace', mb: 1 }}>
            {evidence.metadataScore.toFixed(2)} + {(evidence.imageEvidence.imageAdjustment ?? 0).toFixed(2)} ={' '}
            {(evidence.combinedScore ?? evidence.metadataScore).toFixed(2)}
          </Typography>
        )}
        <Typography sx={{ fontSize: 11.5, color: theme.palette.text.secondary }}>
          The match score is an unbounded sum of the signals above — it can go higher than 0.60 + all
          bonuses, or lower than zero, depending on how many signals apply.
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};
